import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  type ActivePromotion,
  type PromotionPlacePayload,
  isPromotionExpired,
  normalizePromotionConfig,
  promotionSlipErrorMessage,
} from '@/types/promotion'
import { iframeCommunication } from '@/utils/iframeCommunication'
import api from '@/api'

export const usePromotionStore = defineStore('promotion', () => {
  const activePromotion = ref<ActivePromotion | null>(null)
  const clearedReason = ref<string | null>(null)
  /** Player opted in to apply the available promotion on the current betslip. */
  const applyToSlip = ref(false)

  const isActive = computed(() => {
    const promo = activePromotion.value
    return !!promo && !isPromotionExpired(promo)
  })

  const isAppliedOnSlip = computed(() => isActive.value && applyToSlip.value)

  function clear(reason = 'cleared') {
    activePromotion.value = null
    applyToSlip.value = false
    clearedReason.value = reason
  }

  function apply(config: unknown, options?: { defaultType?: ActivePromotion['type'] }) {
    const normalized = normalizePromotionConfig(config, options)
    if (!normalized || isPromotionExpired(normalized)) {
      clear('invalid_or_expired')
      return false
    }
    activePromotion.value = normalized
    applyToSlip.value = false
    clearedReason.value = null
    return true
  }

  function setApplyToSlip(value: boolean) {
    applyToSlip.value = value
  }

  function isModuleEnabled(enabledModules: string[] | undefined): boolean {
    return Array.isArray(enabledModules) && enabledModules.includes('promotions')
  }

  function resolvePromotionSource(settings?: { promotion_source?: string }): string {
    return String(settings?.promotion_source ?? 'bitville')
  }

  function matchesSessionCurrency(currency: string): boolean {
    if (!activePromotion.value) return false
    return activePromotion.value.currency.toUpperCase() === currency.toUpperCase()
  }

  function isEligibleGameSlug(slug: string | undefined | null): boolean {
    const promo = activePromotion.value
    if (!promo) return false
    if (!promo.eligibleGameSlugs?.length) return true
    const normalized = String(slug || '').trim().toLowerCase()
    return !!normalized && promo.eligibleGameSlugs.includes(normalized)
  }

  function validateSlip(options: {
    lineCount: number
    totalStake: number
    currency: string
    gameSlugs: string[]
  }): string | null {
    if (!isAppliedOnSlip.value || !activePromotion.value) return null
    const promo = activePromotion.value

    if (!matchesSessionCurrency(options.currency)) {
      return promotionSlipErrorMessage(promo, 'currency_mismatch', options.lineCount)
    }
    if (promo.singleLineOnly && options.lineCount !== 1) {
      return promotionSlipErrorMessage(promo, 'single_line', options.lineCount)
    }
    if (Math.abs(options.totalStake - promo.stake) > 0.009) {
      return promotionSlipErrorMessage(promo, 'stake_mismatch', options.lineCount)
    }
    for (const slug of options.gameSlugs) {
      if (!isEligibleGameSlug(slug)) {
        return promotionSlipErrorMessage(promo, 'game_ineligible', options.lineCount)
      }
    }
    return null
  }

  function getPlaceBetPayload(): PromotionPlacePayload | null {
    if (!isAppliedOnSlip.value || !activePromotion.value) return null
    const promo = activePromotion.value
    return {
      type: promo.type,
      id: promo.id,
      token: promo.token,
      stake: promo.stake,
    }
  }

  function notifyApplied(payload: { betCount: number; gameSlugs: string[] }) {
    if (!isAppliedOnSlip.value || !activePromotion.value) return
    iframeCommunication.emitIntegrationEvent('promotion-applied', {
      type: activePromotion.value.type,
      id: activePromotion.value.id,
      stake: activePromotion.value.stake,
      betCount: payload.betCount,
      gameSlugs: payload.gameSlugs,
    })
  }

  async function claimFromBitville(
    grantId?: string,
    promotionType?: string
  ): Promise<{ applied: boolean; error?: string }> {
    try {
      const payload: Record<string, string> = grantId ? { grant_id: grantId } : {}
      if (promotionType) {
        payload.type = promotionType
      }
      const { data } = await api.post<{
        status: string
        result?: { promotion?: unknown }
        messages?: string[]
      }>('/promotions/claim', payload)
      const raw = data?.result?.promotion
      if (!raw) {
        return { applied: false }
      }
      const applied = apply(raw)
      return { applied }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { messages?: string[] } }; message?: string }
      const msg =
        err.response?.data?.messages?.[0] ||
        err.message ||
        'Could not load your promotion.'
      return { applied: false, error: msg }
    }
  }

  function shouldUseBitvilleSource(source: string | undefined): boolean {
    const normalized = String(source || 'bitville').toLowerCase()
    return normalized === 'bitville' || normalized === 'hybrid'
  }

  function shouldIgnoreIframePromo(source: string | undefined): boolean {
    return String(source || '').toLowerCase() === 'bitville'
  }

  return {
    activePromotion,
    clearedReason,
    applyToSlip,
    isActive,
    isAppliedOnSlip,
    apply,
    clear,
    setApplyToSlip,
    isModuleEnabled,
    matchesSessionCurrency,
    isEligibleGameSlug,
    validateSlip,
    getPlaceBetPayload,
    notifyApplied,
    claimFromBitville,
    shouldUseBitvilleSource,
    shouldIgnoreIframePromo,
    resolvePromotionSource,
  }
})
